<p-toast></p-toast>

<app-page-shell>
  <app-page-hero eyebrow="Fleet" title="Operational Profitability" subtitle="Owned and rented vehicle P&L with driver performance signals.">
    <div actions>
      <p-button icon="pi pi-refresh" label="Refresh" (onClick)="load()" [loading]="isLoading"></p-button>
      <p-button icon="pi pi-filter-slash" label="Clear" severity="secondary" [outlined]="true" (onClick)="clearFilters()"></p-button>
    </div>
  </app-page-hero>

  <app-page-metric-strip [metrics]="metrics"></app-page-metric-strip>

  <app-page-data-panel title="Filters" subtitle="Tenant is resolved from your authenticated company.">
    <div class="profit-filters">
      <label>
        <span>Period</span>
        <p-datepicker
          [(ngModel)]="dateRange"
          selectionMode="range"
          appendTo="body"
          styleClass="w-full"
          (onSelect)="onPeriodChange()">
        </p-datepicker>
      </label>
      <label>
        <span>Source</span>
        <p-select
          [options]="sourceOptions"
          [(ngModel)]="filters.resourceSource"
          optionLabel="label"
          optionValue="value"
          appendTo="body"
          (onChange)="load()"
          styleClass="w-full">
        </p-select>
      </label>
      <label>
        <span>Driver</span>
        <p-select
          [options]="driverOptions"
          [(ngModel)]="filters.driverId"
          optionLabel="label"
          optionValue="value"
          [filter]="true"
          filterBy="label"
          [showClear]="true"
          placeholder="All drivers"
          appendTo="body"
          (onChange)="load()"
          styleClass="w-full">
        </p-select>
      </label>
      <label>
        <span>Vehicle</span>
        <p-select
          [options]="vehicleOptions"
          [(ngModel)]="filters.vehicleId"
          optionLabel="label"
          optionValue="value"
          [filter]="true"
          filterBy="label"
          [showClear]="true"
          placeholder="All vehicles"
          appendTo="body"
          (onChange)="load()"
          styleClass="w-full">
        </p-select>
      </label>
      <label>
        <span>Vehicle Type</span>
        <p-select
          [options]="vehicleTypeOptions"
          [(ngModel)]="filters.vehicleTypeId"
          optionLabel="label"
          optionValue="value"
          [filter]="true"
          filterBy="label"
          [showClear]="true"
          placeholder="All vehicle types"
          appendTo="body"
          (onChange)="load()"
          styleClass="w-full">
        </p-select>
      </label>
    </div>
  </app-page-data-panel>

  <!-- Profitability KPI Charts -->
  <div class="profit-charts-grid">
    <app-page-data-panel title="Profitability by Ownership" subtitle="Revenue, total cost, and net profit across Owned, Rental, and Freelance assets.">
      <div class="chart-container" *ngIf="hasVehicleData; else emptyVehicleChart">
        <p-chart type="bar" [data]="sourcePnlChartData" [options]="groupedBarChartOptions" height="260px"></p-chart>
      </div>
      <ng-template #emptyVehicleChart>
        <div class="chart-empty">No vehicle profitability data in selected period</div>
      </ng-template>
    </app-page-data-panel>

    <app-page-data-panel title="Cost Structure Breakdown" subtitle="Proportion of trip, fuel, maintenance, rental, and workforce expenses.">
      <div class="chart-container" *ngIf="hasVehicleData; else emptyCostChart">
        <p-chart type="doughnut" [data]="costBreakdownChartData" [options]="doughnutChartOptions" height="260px"></p-chart>
      </div>
      <ng-template #emptyCostChart>
        <div class="chart-empty">No cost data in selected period</div>
      </ng-template>
    </app-page-data-panel>

    <app-page-data-panel title="Vehicle Profit & Loss Leaders" subtitle="Top profit-generating vehicles vs highest loss-making assets.">
      <div class="chart-container" *ngIf="hasVehicleData; else emptyRankingChart">
        <p-chart type="bar" [data]="vehicleRankingChartData" [options]="horizontalBarChartOptions" height="260px"></p-chart>
      </div>
      <ng-template #emptyRankingChart>
        <div class="chart-empty">No vehicle performance data in selected period</div>
      </ng-template>
    </app-page-data-panel>

    <app-page-data-panel title="Driver Profit Contribution" subtitle="Leading drivers by net operational margin contribution.">
      <div class="chart-container" *ngIf="hasDriverData; else emptyDriverChart">
        <p-chart type="bar" [data]="driverContributionChartData" [options]="driverBarChartOptions" height="260px"></p-chart>
      </div>
      <ng-template #emptyDriverChart>
        <div class="chart-empty">No driver performance data in selected period</div>
      </ng-template>
    </app-page-data-panel>
  </div>

  <app-page-data-panel title="Vehicle Profitability" [resultText]="vehicleRows.length + ' vehicles'">
    <p-table [value]="vehicleRows" [loading]="isLoading" [paginator]="true" [rows]="15" styleClass="p-datatable-sm">
      <ng-template pTemplate="header">
        <tr>
          <th>Vehicle</th>
          <th>Source</th>
          <th class="text-right">Deliveries</th>
          <th class="text-right">Revenue</th>
          <th class="text-right">Trip Cost</th>
          <th class="text-right">Maintenance</th>
          <th class="text-right">Fuel</th>
          <th class="text-right">Rental</th>
          <th class="text-right">Total Cost</th>
          <th class="text-right">Profit</th>
          <th class="text-right">Margin</th>
        </tr>
      </ng-template>
      <ng-template pTemplate="body" let-row>
        <tr>
          <td>
            <strong>{{ row.plateNumber || ('Vehicle #' + row.vehicleId) }}</strong>
            <span class="muted">{{ row.registrationNumber || '-' }}</span>
          </td>
          <td><p-tag [value]="row.resourceSource || 'OWNED'" [severity]="sourceSeverity(row.resourceSource)"></p-tag></td>
          <td class="text-right">{{ row.deliveryCount || 0 }}</td>
          <td class="text-right">{{ money(row.shipmentRevenue) }}</td>
          <td class="text-right">{{ money(row.directTripCost) }}</td>
          <td class="text-right">{{ money(row.maintenanceCost) }}</td>
          <td class="text-right">{{ money(row.fuelCost) }}</td>
          <td class="text-right">{{ money(row.rentalCost) }}</td>
          <td class="text-right">{{ money(row.totalCost) }}</td>
          <td class="text-right" [class.loss]="(row.profit || 0) < 0">{{ money(row.profit) }}</td>
          <td class="text-right">{{ percent(row.profitMarginPercent) }}</td>
        </tr>
      </ng-template>
      <ng-template pTemplate="emptymessage">
        <tr>
          <td colspan="11" class="empty-row">No vehicle profitability rows found.</td>
        </tr>
      </ng-template>
    </p-table>
  </app-page-data-panel>

  <app-page-data-panel title="Driver Performance" [resultText]="driverRows.length + ' drivers'">
    <p-table [value]="driverRows" [loading]="isLoading" [paginator]="true" [rows]="15" styleClass="p-datatable-sm">
      <ng-template pTemplate="header">
        <tr>
          <th>Driver</th>
          <th class="text-right">Deliveries</th>
          <th class="text-right">Completed</th>
          <th class="text-right">Completion</th>
          <th class="text-right">Revenue</th>
          <th class="text-right">Trip Cost</th>
          <th class="text-right">Workforce Cash</th>
          <th class="text-right">Mobilization</th>
          <th class="text-right">Contribution</th>
          <th class="text-right">Maintenance</th>
        </tr>
      </ng-template>
      <ng-template pTemplate="body" let-row>
        <tr>
          <td>
            <strong>{{ row.driverName || ('Driver #' + row.driverId) }}</strong>
            <span class="muted">{{ row.assignedVehicleCount || 0 }} assigned vehicles</span>
          </td>
          <td class="text-right">{{ row.deliveryCount || 0 }}</td>
          <td class="text-right">{{ row.completedDeliveryCount || 0 }}</td>
          <td class="text-right">{{ percent(row.completionRatePercent) }}</td>
          <td class="text-right">{{ money(row.shipmentRevenue) }}</td>
          <td class="text-right">{{ money(row.directTripCost) }}</td>
          <td class="text-right">{{ money(row.workforceCashCost) }}</td>
          <td class="text-right">{{ money(row.mobilizationCost) }}</td>
          <td class="text-right" [class.loss]="(row.profitContribution || 0) < 0">{{ money(row.profitContribution) }}</td>
          <td class="text-right">{{ (row.maintenanceRequestCount || 0) + (row.maintenanceWorkOrderCount || 0) }}</td>
        </tr>
      </ng-template>
      <ng-template pTemplate="emptymessage">
        <tr>
          <td colspan="10" class="empty-row">No driver performance rows found.</td>
        </tr>
      </ng-template>
    </p-table>
  </app-page-data-panel>
</app-page-shell>
